import type React from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Empty } from 'antd';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { SplitRow } from './setup';

const TOP_N = 6;
const OTHERS_KEY = '__others__';

// AntD 4 categorical palette (@ant-design/colors -*-6). Picked for max
// hue separation so slices remain distinguishable in the donut chart.
const PALETTE = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];
const OTHERS_COLOR = '#bfbfbf';

const formatMt = (value: number) =>
  `${(value / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MT`;

interface Slice extends SplitRow {
  isOthers?: boolean;
}

interface Props {
  data: SplitRow[];
  // When provided, non-"Others" slices become clickable (used to navigate to a
  // filtered Waybills page, mirroring the table row click behaviour).
  onSliceClick?: (name: string) => void;
}

export const SplitPieChart: React.FC<Props> = ({ data, onSliceClick }) => {
  const intl = useIntl();

  const { slices, total } = useMemo(() => {
    if (data.length === 0) return { slices: [] as Slice[], total: 0 };
    const sorted = [...data].sort((a, b) => b.weight - a.weight);
    const sum = sorted.reduce((acc, r) => acc + r.weight, 0);
    if (sorted.length <= TOP_N + 1) return { slices: sorted as Slice[], total: sum };
    const top = sorted.slice(0, TOP_N) as Slice[];
    const tail = sorted.slice(TOP_N);
    const tailWeight = tail.reduce((acc, r) => acc + r.weight, 0);
    return {
      slices: [
        ...top,
        {
          key: OTHERS_KEY,
          name: intl.formatMessage({ id: 'dashboard.others' }),
          weight: tailWeight,
          isOthers: true,
        },
      ],
      total: sum,
    };
  }, [data, intl]);

  if (slices.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  // Recharts wraps each slice in a sector data object; the raw datum is on
  // `.payload`. Some recharts versions also spread payload fields up to the
  // top level — read both to stay version-agnostic.
  const handleClick = (sector: any) => {
    const slice: Slice = sector?.payload ?? sector;
    if (!slice || slice.isOthers || !onSliceClick) return;
    onSliceClick(slice.name);
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={slices}
          dataKey="weight"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={1}
          // Guarantee that tiny slices (<0.5% of total) remain visible.
          // Real value is still in the tooltip/legend.
          minAngle={2}
          onClick={onSliceClick ? handleClick : undefined}
          isAnimationActive={false}
        >
          {slices.map((slice, idx) => (
            <Cell
              key={slice.key}
              fill={slice.isOthers ? OTHERS_COLOR : PALETTE[idx % PALETTE.length]}
              cursor={onSliceClick && !slice.isOthers ? 'pointer' : 'default'}
              stroke="#fff"
              strokeWidth={1}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: number, name: string) => [
            `${formatMt(value)} (${total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'}%)`,
            name,
          ]}
        />
        <Legend verticalAlign="bottom" height={36} />
      </PieChart>
    </ResponsiveContainer>
  );
};
